import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { getTheme, setTheme } from "@/lib/theme";
import {
  LayoutDashboard,
  Image,
  Layers,
  ScrollText,
  Building2,
  Package,
  LifeBuoy,
  LogOut,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Geräteübersicht", icon: LayoutDashboard, testid: "nav-fleet", end: true },
  { to: "/media", label: "Mediathek", icon: Image, testid: "nav-media" },
  { to: "/gruppen", label: "Gruppen", icon: Layers, testid: "nav-groups" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support", badge: true },
  { to: "/audit", label: "Audit-Log", icon: ScrollText, testid: "nav-audit" },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setThemeState] = useState(getTheme());
  const [ticketBadge, setTicketBadge] = useState(0);

  useEffect(() => {
    const fetchBadge = () => api.get("/tickets/badge").then((r) => setTicketBadge(r.data.count || 0)).catch(() => {});
    fetchBadge();
    const t = setInterval(fetchBadge, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
      isActive
        ? "bg-cyan-500/10 text-cyan-300 border-l-2 border-cyan-400"
        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-l-2 border-transparent"
    }`;

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  }

  const sidebar = (
    <>
      <div className="px-5 py-5 border-b border-border">
        <div className="font-display font-extrabold tracking-tight text-slate-100 text-lg leading-none">
          REX<span className="text-cyan-400">//</span>SIGNAGE
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-2">
          Cloud Control Plane
        </div>
      </div>
      <nav className="flex-1 py-4 space-y-0.5 px-3 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={linkCls} data-testid={item.testid}>
            <item.icon size={15} strokeWidth={1.75} />
            <span className="flex-1">{item.label}</span>
            {item.badge && ticketBadge > 0 && (
              <span
                className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                data-testid="support-badge"
              >
                {ticketBadge}
              </span>
            )}
          </NavLink>
        ))}
        {user?.role === "SUPER_ADMIN" && (
          <>
            <NavLink to="/tenants" className={linkCls} data-testid="nav-tenants">
              <Building2 size={15} strokeWidth={1.75} />
              Mandanten
            </NavLink>
            <NavLink to="/updates" className={linkCls} data-testid="nav-updates">
              <Package size={15} strokeWidth={1.75} />
              Software-Updates
            </NavLink>
          </>
        )}
      </nav>
      <div className="p-3 border-t border-border space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="min-w-0">
            <div className="text-xs text-slate-300 truncate" data-testid="current-user-email">{user?.email}</div>
            <div className="font-mono text-[10px] text-cyan-400/80 tracking-widest" data-testid="current-user-role">{user?.role}</div>
          </div>
          <div className="flex items-center">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-300 hover:text-cyan-300 transition-colors duration-150"
              title={theme === "dark" ? "Helles Design" : "Dunkles Design"}
              data-testid="theme-quick-toggle"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="p-2 text-slate-300 hover:text-red-400 transition-colors duration-150"
              title="Abmelden"
              data-testid="logout-button"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="app-layout">
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <div className="font-display font-extrabold tracking-tight text-slate-100">
          REX<span className="text-cyan-400">//</span>SIGNAGE
        </div>
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 text-slate-400 hover:text-cyan-300"
          data-testid="mobile-menu-button"
          aria-label="Menü öffnen"
        >
          <Menu size={20} />
        </button>
      </div>

      <aside className="hidden lg:flex w-60 shrink-0 border-r border-border bg-card flex-col fixed inset-y-0 z-30">
        {sidebar}
      </aside>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-menu">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-overlay"
          />
          <aside className="absolute left-0 inset-y-0 w-64 bg-card border-r border-border flex flex-col">
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-4 right-3 p-1.5 text-slate-300 hover:text-slate-200 z-10"
              data-testid="mobile-menu-close"
              aria-label="Menü schließen"
            >
              <X size={18} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 lg:ml-60 pt-16 lg:pt-0 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
